@if (active()) {
  <div class="pre-app__panel">
    <div class="step-head">
      <h2 class="step-head__title">Bank Statements</h2>
      <p class="step-head__lead">
        Upload your most recent bank statements for every business bank account you operate. We ask
        for <strong>6–9 months</strong> — the more you share, the stronger and faster your decision.
      </p>
    </div>

    <ng-container [ngTemplateOutlet]="consentBlock()" />

    @for (doc of BANK_DOCS; track doc.type.value) {
      <div class="checklist__row" [class.checklist__row--done]="bankUploadTag() === 'Done'">
        <div class="checklist__row-head">
          <span class="bank-step-title">
            <span class="bank-step-number" aria-hidden="true">1</span>
            <span class="checklist__row-title">{{ doc.type.label }}</span>
          </span>
          @if (bankUploadTag(); as tag) {
            <span class="checklist__tag" data-testid="bank-upload-tag">{{ tag }}</span>
          } @else {
            <span class="checklist__tag checklist__tag--required">{{
              requirementLabel(doc.requirement)
            }}</span>
          }
        </div>
        <p class="checklist__why">{{ doc.why }}</p>
        <app-doc-drop
          [docType]="doc.type"
          [uploads]="uploads()"
          [deletingIds]="deletingIds()"
          (filesPicked)="filesPicked.emit($event)"
          (remove)="remove.emit($event)"
        />

        <!-- What is on file and what to fix: the accounts stay shut until it's dealt with. -->
        @if (bankStatementUploads().length > 0) {
          <div class="bank-guidance" role="status" data-testid="bank-guidance">
            @if (readingStatements().length > 0) {
              <p class="bank-guidance__line">
                <app-icon name="info" [size]="16" />
                <span>
                  Reading {{ readingStatements().length }} statement{{
                    readingStatements().length === 1 ? '' : 's'
                  }}…
                </span>
              </p>
            }
            @for (g of statementGuidance(); track g.id) {
              <p class="bank-guidance__line" [attr.data-testid]="'bank-guidance-' + g.id">
                <app-icon [name]="g.needsMore ? 'info' : 'check'" [size]="16" />
                <span>
                  @if (g.account; as account) {
                    <strong>{{ account }}:</strong>
                  }
                  {{ g.message }}
                </span>
              </p>
            }
            @for (name of problemStatements(); track name) {
              <p class="bank-guidance__line" data-testid="bank-guidance-problem">
                <app-icon name="info" [size]="16" />
                <span>
                  <strong>{{ name }}:</strong>
                  this doesn’t look right (see the note above). Please remove it and upload the
                  correct statement.
                </span>
              </p>
            }
            @for (name of failedUploads(); track name) {
              <p class="bank-guidance__line" data-testid="bank-guidance-failed">
                <app-icon name="info" [size]="16" />
                <span>
                  <strong>{{ name }}:</strong>
                  this didn’t upload. Please remove it and try again.
                </span>
              </p>
            }
            @if (uncountedStatementCount() > 0) {
              <p class="bank-guidance__line" data-testid="bank-guidance-uncounted">
                <app-icon name="info" [size]="16" />
                <span>
                  We couldn’t read the account or dates on
                  {{
                    uncountedStatementCount() === 1
                      ? '1 statement'
                      : uncountedStatementCount() + ' statements'
                  }}, so {{ uncountedStatementCount() === 1 ? 'it isn’t' : 'they aren’t' }}
                  counted here.
                </span>
              </p>
            }
          </div>
          @if (statementsNeedAttention()) {
            <div class="bank-guidance__carry-on" data-testid="bank-carry-on">
              <p class="muted">
                Please sort these out before we check your accounts. If you can’t, you can carry on
                with what you’ve uploaded. The more you share, the stronger and faster your
                decision.
              </p>
              <button
                mat-stroked-button
                type="button"
                (click)="carryOn()"
                data-testid="bank-carry-on-button"
              >
                Carry on with what I’ve uploaded
              </button>
            </div>
          } @else if (affirmation() === null && accountsOpen()) {
            <button
              mat-button
              type="button"
              class="bank-guidance__next"
              (click)="goToAccounts()"
              data-testid="bank-go-to-accounts"
            >
              <span>Next: check your accounts</span>
              <app-icon name="chevron-down" [size]="16" />
            </button>
          }
        }
      </div>
    }

    <!--
      ===== Your business bank accounts (FC-DET-047, Constitution v2.63) =====
      The list is DERIVED: the system reads the account off each
      statement's own face and declares it; the client REVIEWS the groups
      — confirms, says whose account it is, corrects a misreading, names
      an account it has not uploaded — and then confirms the list is
      complete. The confirmation is a BUTTON and never a pre-ticked box.
      It appears with the first upload and opens once every statement is
      read and any problem with them is fixed or knowingly carried past.
      🚨 Nothing here is a fault: an unread statement waits, an omitted
      account is not non-disclosure, and no copy may say otherwise.
    -->
    @if (showAccountReview()) {
      <section
        class="accounts"
        [class.accounts--confirmed]="affirmation() !== null"
        [class.accounts--locked]="!accountsOpen()"
        data-testid="declared-accounts"
      >
        <div class="accounts__head">
          <h3 #accountsHeading class="accounts__title" tabindex="-1">
            <span class="bank-step-number" aria-hidden="true">2</span>
            Check your accounts
          </h3>
          <span class="checklist__tag" data-testid="accounts-tag">{{ accountsTag() }}</span>
        </div>
        @if (accountsOpen()) {
          <p class="muted accounts__lead">
            We pick the accounts up from your statements. Check the list is complete, so that money
            you move between your own accounts isn’t counted as income.
          </p>
        }

        <!-- Only the first read: a re-read as statements land must not blank the list. -->
        @if (!accountsOpen()) {
          <p class="muted accounts__lead" data-testid="accounts-locked">
            {{
              statementsSettling()
                ? 'This opens once we’ve read all your statements.'
                : 'This opens once the statements above are sorted out.'
            }}
          </p>
        } @else if (accountsLoading() && declaredAccounts().length === 0) {
          <p class="muted">Loading…</p>
        } @else if (accountsError()) {
          <div class="panel-error">
            <p>Couldn't load your bank accounts.</p>
            <button mat-stroked-button type="button" (click)="retryDeclarations()">
              Try again
            </button>
          </div>
        } @else {
          <!-- One group per account the statements (or the client) named. -->
          <ul class="accounts__groups">
            @for (g of accountGroups(); track g.account.short_id) {
              <li class="accounts__group" [attr.data-testid]="'account-' + g.row.short_id">
                <div class="accounts__group-head">
                  <div class="accounts__row-main">
                    <span class="accounts__row-title">{{ accountLabel(g.row) }}</span>
                    <span class="muted">
                      {{ g.row.holder_name || g.account.account_holder_name }}
                      @if (holderLabel(g.row); as held) {
                        · {{ held }}
                      }
                    </span>
                    <span
                      class="accounts__tag"
                      [attr.data-testid]="'account-source-' + g.row.short_id"
                    >
                      {{
                        g.row.derived
                          ? 'Read from your statements'
                          : g.row.not_uploaded_reason
                            ? 'Named — no statements uploaded'
                            : 'Added by you'
                      }}
                    </span>
                  </div>
                  <div class="accounts__group-actions">
                    @if (g.row.derived || g.statements.length > 0) {
                      <button
                        mat-button
                        type="button"
                        (click)="startCorrection(g)"
                        [attr.data-testid]="'account-correct-' + g.row.short_id"
                      >
                        This isn’t right
                      </button>
                    }
                    <button
                      mat-button
                      type="button"
                      (click)="withdrawAccount(g.row)"
                      [attr.data-testid]="'account-withdraw-' + g.row.short_id"
                    >
                      {{ g.row.derived ? 'Not this business’s account' : 'Remove' }}
                    </button>
                  </div>
                </div>

                @if (g.row.not_uploaded_reason) {
                  <p class="muted accounts__reason">
                    Why no statements: {{ g.row.not_uploaded_reason }}
                  </p>
                }

                @if (g.statements.length > 0) {
                  <ul class="accounts__statements">
                    @for (st of g.statements; track st.uploadId) {
                      <li class="accounts__statement">
                        <app-icon name="file-text" [size]="14" />
                        <span class="accounts__statement-name">{{ st.fileName }}</span>
                        @if (st.face?.period_start && st.face?.period_end) {
                          <span class="muted"
                            >{{ st.face?.period_start | date: 'd MMM' }} –
                            {{ st.face?.period_end | date: 'd MMM yyyy' }}</span
                          >
                        }
                      </li>
                    }
                  </ul>
                  @if (g.periods.length > 0) {
                    <p class="muted accounts__periods" data-testid="account-periods">
                      {{ g.statements.length }} statement{{
                        g.statements.length === 1 ? '' : 's'
                      }}
                      on file: {{ g.periods[0] }}
                      @if (g.periods.length > 1) {
                        to {{ g.periods[g.periods.length - 1] }}
                      }
                    </p>
                  }
                }

                <!--
                  Whose account? Put only where the printed holder is not
                  the business and nobody has said. A question, not a
                  warning: a director’s account uploaded here is ordinary.
                -->
                @if (g.holderQuestion) {
                  <div class="accounts__holder" data-testid="account-holder-question">
                    <span class="accounts__subtitle">Whose account is this?</span>
                    <div class="accounts__holder-fields">
                      <mat-form-field appearance="outline" class="accounts__holder-select">
                        <mat-label>Held by</mat-label>
                        <mat-select
                          [value]="holderAnswer()[g.row.short_id] ?? ''"
                          (valueChange)="setHolderAnswer(g.row.short_id, $event)"
                          [attr.data-testid]="'account-holder-' + g.row.short_id"
                        >
                          @for (opt of holderChoices; track opt.value) {
                            <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                      @if (
                        partyOptions().length > 0 &&
                        (holderAnswer()[g.row.short_id] === 'DIRECTOR_OR_SHAREHOLDER' ||
                          holderAnswer()[g.row.short_id] === 'RELATED_BUSINESS')
                      ) {
                        <mat-form-field appearance="outline" class="accounts__holder-select">
                          <mat-label>Who</mat-label>
                          <mat-select
                            [value]="holderPartyAnswer()[g.row.short_id] ?? null"
                            (valueChange)="setHolderParty(g.row.short_id, $event)"
                          >
                            <mat-option [value]="null">Not listed</mat-option>
                            @for (party of partyOptions(); track party.short_id) {
                              <mat-option [value]="party.short_id">{{ party.label }}</mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                      }
                      <button
                        mat-stroked-button
                        type="button"
                        [disabled]="
                          !holderAnswer()[g.row.short_id] || savingGroupId() === g.row.short_id
                        "
                        (click)="answerHolder(g)"
                        [attr.data-testid]="'account-holder-save-' + g.row.short_id"
                      >
                        {{ savingGroupId() === g.row.short_id ? 'Saving…' : 'Save' }}
                      </button>
                    </div>
                  </div>
                }

                <!-- "This isn’t right": the identity re-typed; the derived reading stays on record. -->
                @if (correctingId() === g.row.short_id) {
                  <form
                    class="accounts__form"
                    [formGroup]="correctionForm"
                    (ngSubmit)="submitCorrection()"
                    data-testid="account-correction-form"
                  >
                    <mat-form-field appearance="outline">
                      <mat-label>Bank</mat-label>
                      <input matInput formControlName="bank_name" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Branch code (if you know it)</mat-label>
                      <input matInput formControlName="branch_code" />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Full account number</mat-label>
                      <input
                        matInput
                        formControlName="account_number"
                        data-testid="account-correction-number"
                      />
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Account holder</mat-label>
                      <input matInput formControlName="account_holder_name" />
                    </mat-form-field>
                    <div class="accounts__form-actions">
                      <button mat-button type="button" (click)="cancelCorrection()">Cancel</button>
                      <button
                        mat-flat-button
                        color="primary"
                        type="submit"
                        [disabled]="savingGroupId() === g.row.short_id"
                        data-testid="account-correction-save"
                      >
                        {{ savingGroupId() === g.row.short_id ? 'Saving…' : 'Save correction' }}
                      </button>
                    </div>
                  </form>
                }
              </li>
            }
          </ul>

          <!--
            Statements nothing has attributed: the check is still running,
            or neither reader could make out the account. The picker is now
            the EXCEPTION, not the rule; "add it by hand" is the other way out.
          -->
          @if (unmatchedStatements().length > 0) {
            <div class="accounts__attrib" data-testid="statement-attribution">
              <h4 class="accounts__subtitle">
                {{
                  unmatchedStatements().length === 1
                    ? 'One statement we couldn’t match to an account yet'
                    : unmatchedStatements().length +
                      ' statements we couldn’t match to an account yet'
                }}
              </h4>
              @for (st of unmatchedStatements(); track st.uploadId) {
                <div class="accounts__attrib-row">
                  <span class="accounts__attrib-name">
                    {{ st.fileName }}
                  </span>
                  @if (distinctDeclaredAccounts().length > 0) {
                    <mat-form-field appearance="outline" class="accounts__attrib-select">
                      <mat-label>Account</mat-label>
                      <mat-select
                        [value]="attributionFor(st.docShortId)"
                        (valueChange)="onAttributionChange(st.docShortId, $event)"
                        [attr.data-testid]="'attribution-' + st.docShortId"
                      >
                        <!--
                          One option per ACCOUNT, not per declaration: the
                          same account declared twice was offered twice, with
                          identical text and an identical value.
                        -->
                        @for (row of distinctDeclaredAccounts(); track row.short_id) {
                          <mat-option [value]="row.bank_account?.short_id">{{
                            accountLabel(row)
                          }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                </div>
              }
              <button
                mat-button
                type="button"
                (click)="toggleAccountForm('manual')"
                data-testid="account-add-manual"
              >
                {{
                  showAccountForm() && accountFormMode() === 'manual'
                    ? 'Cancel'
                    : 'Add the account by hand'
                }}
              </button>
              @if (unattributedCount() > 0) {
                <p class="muted" data-testid="unattributed-note">
                  {{ unattributedCount() }} statement(s) not yet matched to an account.
                </p>
              }
            </div>
          }

          <button
            mat-button
            type="button"
            class="accounts__name-toggle"
            (click)="toggleAccountForm('named')"
            data-testid="account-add-toggle"
          >
            {{
              showAccountForm() && accountFormMode() === 'named'
                ? 'Cancel'
                : 'Name an account you haven’t uploaded'
            }}
          </button>

          @if (showAccountForm()) {
            <form
              class="accounts__form"
              [formGroup]="accountForm"
              (ngSubmit)="addAccount()"
              data-testid="account-form"
            >
              <mat-form-field appearance="outline">
                <mat-label>Bank</mat-label>
                <input matInput formControlName="bank_name" data-testid="account-bank-name" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Branch code (if you know it)</mat-label>
                <input matInput formControlName="branch_code" data-testid="account-branch" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Account number</mat-label>
                <input matInput formControlName="account_number" data-testid="account-number" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Account holder</mat-label>
                <input
                  matInput
                  formControlName="account_holder_name"
                  data-testid="account-holder"
                />
              </mat-form-field>
              <!--
                A select and not a checkbox, on purpose: "Not sure" has to be
                sayable, and an unticked box would record "no trading on this
                account" — a claim the client never made.
              -->
              <mat-form-field appearance="outline">
                <mat-label>Does trading go through it?</mat-label>
                <mat-select formControlName="carries_trading_activity">
                  @for (opt of tradingStates; track opt.value) {
                    <mat-option [value]="opt.value">{{ opt.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              @if (accountFormMode() === 'named') {
                <!-- FC-DET-047’s named limb: an account operated but not uploaded, with the reason. -->
                <mat-form-field appearance="outline" class="accounts__form-wide">
                  <mat-label>Why haven’t you uploaded statements for it?</mat-label>
                  <input
                    matInput
                    formControlName="not_uploaded_reason"
                    data-testid="account-reason"
                  />
                </mat-form-field>
              }
              <div class="accounts__form-actions">
                <button
                  mat-flat-button
                  color="primary"
                  type="submit"
                  [disabled]="isAddingAccount()"
                  data-testid="account-save"
                >
                  {{
                    isAddingAccount()
                      ? 'Saving…'
                      : accountFormMode() === 'named'
                        ? 'Name this account'
                        : 'Add account'
                  }}
                </button>
              </div>
            </form>
          }

          <!--
            ===== The completeness confirmation =====
            🚨 A button the client presses, never a default-on checkbox. One
            account on the list is not every account, so what makes this
            meaningful is that it cannot be made by accident. It is
            withdrawable, and any change to the list retracts it — which
            the copy below says out loud rather than letting the client
            believe a stale confirmation still stands.
          -->
          <div class="accounts__affirm" data-testid="account-affirmation">
            @if (affirmation(); as a) {
              <p class="accounts__affirm-done" data-testid="affirmation-standing">
                <app-icon name="check" [size]="16" />
                <span>
                  Confirmed complete by {{ a.declared_by_name }} on
                  {{ a.declared_at | date: 'mediumDate' }}.
                </span>
              </p>
              <button
                mat-button
                type="button"
                (click)="withdrawAffirmation()"
                data-testid="affirmation-withdraw"
              >
                Withdraw this confirmation
              </button>
            } @else {
              @if (affirmationWasWithdrawn()) {
                <p class="accounts__affirm-note" data-testid="affirmation-withdrawn-note">
                  <app-icon name="info" [size]="16" />
                  <span>
                    Your earlier confirmation no longer stands because the account list changed.
                    Confirm again when the list is right.
                  </span>
                </p>
              }
              <button
                mat-flat-button
                color="primary"
                type="button"
                [disabled]="!canAffirm() || isAffirming()"
                (click)="affirm()"
                data-testid="affirmation-confirm"
              >
                {{
                  isAffirming()
                    ? 'Saving…'
                    : 'I’ve uploaded statements for every business bank account we operate'
                }}
              </button>
              @if (declaredAccounts().length === 0) {
                <p class="muted">Upload a statement, or name an account, before confirming.</p>
              }
            }
          </div>

          @if (accountNote(); as note) {
            <p class="accounts__note" role="status" data-testid="account-already-declared">
              <app-icon name="info" [size]="16" />
              <span>{{ note }}</span>
            </p>
          }

          @if (accountError(); as error) {
            <p class="panel-error accounts__note" role="alert" data-testid="account-error">
              <span>{{ error }}</span>
            </p>
          }
        }
      </section>
    }

    @if (bankStepBlockedReason(); as reason) {
      <p class="muted" role="status" data-testid="bank-next-reason">{{ reason }}</p>
    }

    <div class="pre-app__nav">
      <span></span>
      <!-- On a wrapper: a disabled button fires no hover or focus of its own. -->
      <span
        class="pre-app__next-wrap"
        [matTooltip]="bankStepBlockedReason() ?? ''"
        [attr.tabindex]="bankStepBlockedReason() ? 0 : null"
        data-testid="step-bank-next-tooltip"
      >
        <button
          mat-flat-button
          color="primary"
          type="button"
          (click)="next.emit()"
          [disabled]="!canLeaveBankStep()"
          data-testid="step-bank-next"
        >
          <span>Proceed to financial information</span>
          <app-icon name="arrow-right" [size]="18" />
        </button>
      </span>
    </div>
  </div>
}
